import React from "react";
import {
  Hash,
  Building,
  Globe,
  User,
  Mail,
  Clock,
} from "lucide-react";
import CharacterCounter from "./CharacterCounter";
import RequiredFieldMarker from "./RequiredFieldMarker";
import { COUNTRIES } from "./constants";

const FieldError = ({ message }) =>
  message ? <p className="text-sm text-red-500 mt-1">{message}</p> : null;

const TIMEZONES = [
  { id: "America/New_York", label: "Eastern Time (ET)" },
  { id: "America/Chicago", label: "Central Time (CT)" },
  { id: "America/Denver", label: "Mountain Time (MT)" },
  { id: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { id: "America/Anchorage", label: "Alaska Time (AKT)" },
  { id: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
  { id: "America/Phoenix", label: "Arizona Time (MST, no DST)" },
];

const SiteFormFields = ({ values, errors, onChange, mode = "add" }) => (
  <>
    {/* Site Code */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Site Code <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Hash className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          name="site_code"
          type="text"
          maxLength={4}
          value={values.site_code}
          onChange={onChange}
          placeholder="Enter site code"
          className={`w-full pl-10 pr-3 py-2 border ${
            errors.site_code ? "border-red-500" : "border-gray-300"
          } rounded-md`}
        />
      </div>
      <CharacterCounter current={values.site_code} max={4} />
      <FieldError message={errors.site_code} />
    </div>

    {/* Institution */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Institution <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Building className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          name="institution"
          type="text"
          maxLength={100}
          value={values.institution}
          onChange={onChange}
          placeholder="Enter institution name"
          className={`w-full pl-10 pr-3 py-2 border ${
            errors.institution ? "border-red-500" : "border-gray-300"
          } rounded-md`}
        />
      </div>
      <CharacterCounter current={values.institution} max={100} />
      <FieldError message={errors.institution} />
    </div>

    {/* Country */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Country <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <select
          name="country"
          value={values.country}
          onChange={onChange}
          className={`w-full pl-10 pr-3 py-2 border ${
            errors.country ? "border-red-500" : "border-gray-300"
          } rounded-md`}
        >
          <option value="">Select a country</option>
          {COUNTRIES.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
      </div>
      <FieldError message={errors.country} />
    </div>

    {/* Contact Person */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Contact Person <RequiredFieldMarker />
      </label>
      <div className="relative">
        <User className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          name="contact_person"
          type="text"
          maxLength={100}
          value={values.contact_person}
          onChange={onChange}
          placeholder="Enter full name"
          className={`w-full pl-10 pr-3 py-2 border ${
            errors.contact_person ? "border-red-500" : "border-gray-300"
          } rounded-md`}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Please enter the contact person’s full name as it should appear officially
      </p>
      <CharacterCounter current={values.contact_person} max={100} />
      <FieldError message={errors.contact_person} />
    </div>

    {/* Contact Email */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Contact Email <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <input
          name="email"
          type="email"
          value={values.email}
          onChange={onChange}
          placeholder="Enter contact email"
          className={`w-full pl-10 pr-3 py-2 border ${
            errors.email ? "border-red-500" : "border-gray-300"
          } rounded-md`}
        />
      </div>
      <FieldError message={errors.email} />
    </div>

    {/* Timezone */}
    <div className="mb-4">
      <label className="block text-sm font-medium mb-1">
        Timezone <RequiredFieldMarker />
      </label>
      <div className="relative">
        <Clock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
        <select
          name="timezone"
          value={values.timezone}
          onChange={onChange}
          className={`w-full pl-10 pr-3 py-2 border ${
            errors.timezone ? "border-red-500" : "border-gray-300"
          } rounded-md`}
        >
          <option value="">Select a timezone</option>
          {TIMEZONES.map((tz) => (
            <option key={tz.id} value={tz.id}>
              {tz.label}
            </option>
          ))}
        </select>
      </div>
      <p className="text-xs text-gray-500 mt-1">
        Participant timezones are derived from this. The timezone is required
        to ensure proper scheduling and notifications in participant’s local time.
      </p>
      <FieldError message={errors.timezone} />
    </div>
  </>
);

export default SiteFormFields;
